import React, { useState, useEffect } from 'react';
import { publicApi } from '@/services/api';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveImage, PROCESS_IMAGE_BASE } from '@/lib/imageUrl';

const ProcesosV2 = () => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProcesses();
  }, []);

  const loadProcesses = async () => {
    try {
      const response = await publicApi.getProcesses();
      setProcesses(response.data);
    } catch (error) {
      console.error('Error loading processes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Hero Banner */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/procesos-header.jpg"
            alt="Procesos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-0.5 bg-v2-orange" />
            <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Fabricación</span>
            <span className="w-12 h-0.5 bg-v2-orange" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Nuestros <span className="text-v2-orange">Procesos</span>
          </h1>
          <p className="text-xl text-v2-text-muted max-w-2xl mx-auto">
            Transformamos ACERO con precisión para proyectos industriales y de construcción.
          </p>
        </div>
      </div>

      {/* Processes Grid */}
      <div className="max-w-7xl mx-auto px-8 py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-v2-gray border border-v2-border flex animate-pulse">
                <div className="w-48 h-48 bg-v2-light-gray" />
                <div className="flex-1 p-6">
                  <div className="h-6 bg-v2-light-gray rounded w-3/4 mb-3" />
                  <div className="h-4 bg-v2-light-gray rounded mb-2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process) => (
              <div 
                key={process.id}
                className="group bg-v2-gray border border-v2-border hover:border-v2-orange flex flex-col sm:flex-row overflow-hidden transition-all duration-300"
                data-testid={`process-card-v2-${process.id}`}
              >
                <div className="sm:w-48 h-48 sm:h-auto overflow-hidden flex-shrink-0">
                  <img 
                    src={resolveImage(process.ruta_img, PROCESS_IMAGE_BASE)}
                    alt={process.nombre}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/1A1A1A/FF6B00?text=Proceso'; }}
                  />
                </div>
                <div className="flex-1 p-6">
                  <h3 className="text-xl font-bold text-white uppercase group-hover:text-v2-orange transition-colors">
                    {process.nombre}
                  </h3>
                  <p className="text-v2-text-muted mt-3 leading-relaxed">
                    {process.descripcion}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <section className="py-20 bg-v2-dark">
        <div className="max-w-7xl mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-12 h-0.5 bg-v2-orange" />
              <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">A la medida</span>
              <span className="w-12 h-0.5 bg-v2-orange" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Fabricación <span className="text-v2-orange">Personalizada</span>
            </h2>
            <p className="text-v2-text-muted text-lg leading-relaxed mb-10">
              En NAYACEROS contamos con la capacidad técnica y operativa para fabricar piezas especiales conforme a las especificaciones de cada proyecto, brindando asesoría especializada en la selección de materiales y procesos de transformación.
            </p>
            {/*<div className="grid sm:grid-cols-3 gap-8">
              <div className="text-center">
                <span className="text-5xl font-bold text-v2-orange">+17</span>
                <p className="text-v2-text-muted mt-2">Años de Experiencia</p>
              </div>
              <div className="text-center">
                <span className="text-5xl font-bold text-v2-orange">CNC</span>
                <p className="text-v2-text-muted mt-2">Maquinaria Especializada</p>
              </div>
              <div className="text-center">
                <span className="text-5xl font-bold text-v2-orange">100%</span>
                <p className="text-v2-text-muted mt-2">Calidad Garantizada</p>
              </div>
            </div>*/}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/procesos/cajas-de-volteo.jpg"
            alt="CTA Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Tienes algún proyecto en <span className="text-v2-orange">puerta</span>?
          </h2>
          <p className="text-v2-text-muted text-lg max-w-2xl mx-auto mb-8">
            Cuéntanos, nosotros te ayudaremos.
          </p>
          <Link 
            to="/contacto" 
            className="group inline-flex items-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider px-10 py-5 transition-all duration-300"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
          >
            Solicitar Información
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProcesosV2;
